import {Plugin} from '@oracle/ortl-core';
import {priorities} from '@oracle/ortl-utils';

import '../theme/resize.css';

/**
 * The editor resize feature.
 *
 * @extends module:core/plugin~Plugin
 */
export default class Resize extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'Resize';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [];
	}

	/**
	 * @inheritDoc
	 */
	constructor(editor) {
		super(editor);

		editor.config.define('resize', {
			direction: Resize.DIRECTION.BOTH,
			userResize: true,
			width: null,
			height: null,
			minWidth: null,
			minHeight: null,
			maxWidth: null,
			maxHeight: null,
		});
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		const config = editor.config.get('resize');

		editor.ui.view.on('render', () => {
			const rootElement = editor.ui.view.element;

			// add styles
			rootElement.classList.add('ck-resize');

			// apply sizes
			['width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight'].forEach((prop) => {
				const value = config[prop];
				if (value !== null) {
					rootElement.style[prop] = coerceSizeValue(value);
				}
			});

			// restrict size of overflow toolbar
			const groupedItemsDropdown = editor.ui.view.toolbar._behavior.groupedItemsDropdown;
			if (groupedItemsDropdown) {
				groupedItemsDropdown.on(
					'change:isOpen',
					(evt, p, isOpen) => {
						if (isOpen) {
							const size = rootElement.getBoundingClientRect();
							groupedItemsDropdown.panelView.element.style.maxWidth = size.width + 'px';
						}
					},
						{priority: priorities.low},
					);
			}

			if (config.userResize) {
				rootElement.classList.add('ck-user-resize');
				rootElement.style.resize = config.direction;
			}
		});
	}

	resize(width, height) {
		const editor = this.editor;
		const config = editor.config.get('resize');

		if (config.minWidth) {
			width = Math.max(width, config.minWidth);
		}
		if (config.maxWidth) {
			width = Math.min(width, config.maxWidth);
		}
		if (config.minHeight) {
			height = Math.max(height, config.minHeight);
		}
		if (config.maxHeight) {
			height = Math.min(height, config.maxHeight);
		}

		const rootElement = this.editor.ui.view.element;
		rootElement.style.width = coerceSizeValue(width);
		rootElement.style.height = coerceSizeValue(height);
		this.fire('resize', {
			width,
			height,
		});
	}

	static DIRECTION = Object.freeze({
		BOTH: 'both',
		HORIZONTAL: 'horizontal',
		VERTICAL: 'vertical',
	});
}

function coerceSizeValue(value) {
	return value + 'px';
}
